import { VerticalFlexbox } from "@src/components/layout/Flexbox";
import { Input } from "@src/components/ui/input";
import { Label } from "@src/components/ui/label";
import { cn } from "@src/lib/utils";
import { useField } from "formik";
import { AsYouType } from "libphonenumber-js";
import { type InputHTMLAttributes, useId } from "react";

const MAX_NATIONAL_NUMBER_LENGTH = 10;

/**
 * Formats the given value into a (maybe partially entered) US phone number,
 * mirroring the behavior of the legacy `<PhoneNumberInputFormik />` component.
 *
 * If the entered national number exceeds the US length, the raw value is
 * returned to preserve the user's input rather than collapsing it back into
 * a digits-only form.
 */
export function formatPhoneNumberAsYouType(value: string): string {
  const formatter = new AsYouType("US");
  const formatted = formatter.input(value);
  const phoneNumber = formatter.getNumber();

  if (
    phoneNumber &&
    phoneNumber.nationalNumber.length > MAX_NATIONAL_NUMBER_LENGTH
  ) {
    return value;
  }

  return formatted;
}

function isDeletingCharacters(oldValue: string, newValue: string): boolean {
  return newValue.length < oldValue.length;
}

/**
 * Returns the next value to store in Formik for a phone number input.
 *
 * Skips formatting when the user is deleting characters; otherwise auto-
 * formats input as a US phone number.
 */
function getNextPhoneValue(newValue: string, oldValue: string): string {
  if (isDeletingCharacters(oldValue, newValue)) {
    return newValue;
  }
  return formatPhoneNumberAsYouType(newValue);
}

export interface FormikPhoneInputProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "name" | "value" | "onChange" | "onBlur" | "type"
> {
  name: string;
  label?: string;
  hint?: string;
  required?: boolean;
  containerClassName?: string;
}

/**
 * Formik-integrated phone-number input. Auto-formats values as the user
 * types using `libphonenumber-js`'s `AsYouType` formatter, defaulting to
 * the `US` country.
 */
export const FormikPhoneInput = ({
  name,
  label,
  hint,
  required,
  containerClassName,
  className,
  id,
  maxLength = 16,
  inputMode = "tel",
  autoComplete = "tel",
  ...props
}: FormikPhoneInputProps) => {
  const [field, meta, helpers] = useField<string>(name);
  const reactId = useId();
  const inputId = id ?? reactId;
  const error = meta.touched && meta.error ? String(meta.error) : undefined;

  return (
    <VerticalFlexbox gap="xs" className={cn("w-full", containerClassName)}>
      {label ? (
        <Label htmlFor={inputId} className="text-sm text-foreground">
          {label}
          {required ? (
            <span className="ml-0.5 text-destructive" aria-hidden>
              *
            </span>
          ) : null}
        </Label>
      ) : null}
      <Input
        {...props}
        id={inputId}
        type="tel"
        name={field.name}
        value={field.value ?? ""}
        onChange={(event) => {
          const next = getNextPhoneValue(event.target.value, field.value ?? "");
          helpers.setValue(next);
        }}
        onBlur={field.onBlur}
        required={required}
        maxLength={maxLength}
        inputMode={inputMode}
        autoComplete={autoComplete}
        aria-invalid={error ? "true" : undefined}
        className={cn(
          error && "border-destructive ring-destructive",
          className,
        )}
      />
      {error ? (
        <span className="text-xs text-destructive">{error}</span>
      ) : hint ? (
        <span className="text-xs text-muted-foreground">{hint}</span>
      ) : null}
    </VerticalFlexbox>
  );
};
